<script setup lang="ts">
/**
 * Хаб проектов — макет 17:765: тулбар с фильтром и поиском, заголовок со
 * сводкой и сетка карточек 354×148 в три колонки.
 *
 * Фильтров три, как в макете: «Все», «Мои» и «Архив». Архивный проект уходит
 * из хаба и из сайдбара, но его задачи остаются доступны по ссылке — это не
 * удаление (DEV-1500).
 *
 * Подсказки «⌘K» у поиска тоже нет: сочетание отдано палитре быстрого перехода
 * (DEV-1482), а два разных действия на одно сочетание не уживаются.
 */
import { AppButton, AppSegmentedControl, AppTextField } from '@vinprop/design-system'
import { AlertTriangle, Folder, Search } from 'lucide-vue-next'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

import TrackerEmptyState from '@/components/TrackerEmptyState.vue'
import TrackerToolbar from '@/components/TrackerToolbar.vue'
import OnboardingCard from '@/features/onboarding/OnboardingCard.vue'
import { ACTIVITY_PAGE, listActivity, type ActivityEvent } from '@/features/projects/activity'
import ActivityFeed from '@/features/projects/components/ActivityFeed.vue'
import {
  buildSteps,
  dismiss,
  doneCount,
  isDismissed,
  loadProgress,
  type Step,
} from '@/features/onboarding/api'
import CreateProjectModal from '@/features/projects/components/CreateProjectModal.vue'
import ProjectCard from '@/features/projects/components/ProjectCard.vue'
import { useProjectsStore, type ProjectFilter } from '@/features/projects/store'

const { t } = useI18n()

const projects = useProjectsStore()
const route = useRoute()
const router = useRouter()

const filter = ref<ProjectFilter>('all')
const search = ref('')
// Плюс в сайдбаре ведёт сюда с ?create=1: заводить проект зовут с любого
// экрана, а форма живёт на хабе.
const isCreateOpen = ref(route.query.create === '1')

const filters = computed(() => [
  { value: 'all' as const, label: t('projects.filters.all') },
  { value: 'mine' as const, label: t('projects.filters.mine') },
  { value: 'archived' as const, label: t('projects.filters.archived') },
])

// Три числительных, а не одна строка: у русского три формы, и «1 проектов»
// на первом же экране нового пользователя выглядит поломкой.
const summary = computed(() =>
  [
    t('projects.summary.projects', projects.items.length),
    t('projects.summary.issues', projects.openIssueTotal),
    t('projects.summary.mine', projects.myProjectCount),
  ].join(' · '),
)

const isEmpty = computed(
  () => !projects.isLoading && !projects.error && projects.items.length === 0,
)

/** Пусто из-за фильтра — это не «нет проектов», и звать заводить первый незачем. */
const isFiltered = computed(() => filter.value !== 'all' || search.value.trim().length > 0)

const reload = () => projects.load({ filter: filter.value, search: search.value.trim() })

let searchTimer: ReturnType<typeof setTimeout> | undefined

// Запрос на каждую букву не нужен: список короткий, а бэкенд ищет по всем
// проектам, до которых человек допущен.
watch(search, () => {
  clearTimeout(searchTimer)
  searchTimer = setTimeout(reload, 300)
})

watch(filter, reload)

/**
 * Чек-лист начала работы. Показываем, пока он не пройден целиком: подсказка
 * нужна первые дни, а потом занимает место на самом посещаемом экране.
 */
const events = ref<ActivityEvent[]>([])
const activityPage = ref(1)
const isLoadingMore = ref(false)
const hasMoreActivity = ref(false)
const steps = ref<Step[]>([])

/**
 * Следующая порция событий. Полная страница означает, что дальше есть ещё:
 * общего числа сервис не отдаёт, а лишний запрос ради счётчика дороже, чем
 * лишний раз показанная кнопка.
 */
const loadActivity = async (page: number) => {
  const portion = await listActivity(page)

  hasMoreActivity.value = portion.length === ACTIVITY_PAGE
  events.value = page === 1 ? portion : [...events.value, ...portion]
  activityPage.value = page
}

const loadMoreActivity = async () => {
  isLoadingMore.value = true

  try {
    await loadActivity(activityPage.value + 1)
  } catch {
    // Догрузка не удалась — уже показанные события остаются на месте.
    hasMoreActivity.value = false
  } finally {
    isLoadingMore.value = false
  }
}
const isOnboardingVisible = computed(
  () => steps.value.length > 0 && doneCount(steps.value) < steps.value.length,
)

const refreshOnboarding = async () => {
  if (isDismissed()) return

  try {
    const next = buildSteps(await loadProgress(projects.items[0]?.id ?? ''))

    steps.value = next

    // Пройденный больше не считаем: два запроса на каждый заход ради строки,
    // которой уже не будет.
    if (doneCount(next) === next.length) dismiss()
  } catch {
    // Подсказка не стоит того, чтобы из-за неё экран показывал ошибку.
    steps.value = []
  }
}

// Список мог приехать раньше — его грузит лэйаут ради сайдбара; второй раз
// за тем же ходить незачем.
onMounted(async () => {
  await projects.ensureLoaded()
  await refreshOnboarding()

  try {
    await loadActivity(1)
  } catch {
    // Лента — второстепенный блок: без неё хаб остаётся рабочим.
    events.value = []
  }
})

// Флаг в адресе одноразовый: перезагрузка страницы не должна снова открывать
// форму, а «назад» — возвращать в неё.
watch(isCreateOpen, (open) => {
  if (!open && route.query.create) void router.replace({ query: {} })
})

onBeforeUnmount(() => {
  clearTimeout(searchTimer)
})
</script>

<template>
  <TrackerToolbar :breadcrumbs="[{ label: t('nav.projects') }]">
    <AppSegmentedControl
      v-model="filter"
      :options="filters"
      size="small"
      :aria-label="t('projects.filters.label')"
    />

    <div class="projects__search">
      <AppTextField
        v-model="search"
        size="small"
        filled
        :placeholder="t('projects.searchPlaceholder')"
        :prefix-icon="Search"
        :aria-label="t('projects.searchPlaceholder')"
      />
    </div>

    <AppButton variant="primary" size="small" @click="isCreateOpen = true">
      {{ t('projects.create.action') }}
    </AppButton>
  </TrackerToolbar>

  <div class="projects">
    <h1 class="projects__title">{{ t('nav.projects') }}</h1>
    <p class="projects__summary">{{ summary }}</p>

    <div v-if="projects.isLoading && !projects.items.length" class="projects__grid">
      <div v-for="index in 3" :key="index" class="projects__skeleton" />
    </div>

    <TrackerEmptyState
      v-else-if="projects.error"
      :icon="AlertTriangle"
      :title="t('projects.error.title')"
      :description="t('projects.error.description')"
      tint="var(--color-danger-tint)"
    >
      <AppButton variant="secondary" @click="reload">{{ t('projects.error.retry') }}</AppButton>
    </TrackerEmptyState>

    <TrackerEmptyState
      v-else-if="isEmpty && isFiltered"
      :icon="Search"
      :title="t('projects.nothingFound.title')"
      :description="t('projects.nothingFound.description')"
    />

    <TrackerEmptyState
      v-else-if="isEmpty"
      :icon="Folder"
      :title="t('projects.empty.title')"
      :description="t('projects.empty.description')"
    >
      <AppButton variant="primary" @click="isCreateOpen = true">
        {{ t('projects.create.action') }}
      </AppButton>
    </TrackerEmptyState>

    <div v-else class="projects__split">
      <div class="projects__grid">
        <ProjectCard
          v-for="project in projects.items"
          :key="project.id"
          :project="project"
          :members="projects.members[project.id]"
        />
      </div>

      <!-- Подсказка и лента стоят сбоку, а не под сеткой (DEV-1590): лента
           росла вниз и уносила «Начало работы» за нижний край. Архивную
           вкладку колонка не сопровождает — там смотрят на список. -->
      <aside v-if="filter !== 'archived'" class="projects__side">
        <OnboardingCard
          v-if="isOnboardingVisible"
          :steps="steps"
          :project-key="projects.items[0]?.key ?? ''"
          @create-project="isCreateOpen = true"
        />

        <ActivityFeed
          :events="events"
          :loading="isLoadingMore"
          :has-more="hasMoreActivity"
          @more="loadMoreActivity"
        />
      </aside>
    </div>
  </div>

  <CreateProjectModal v-model="isCreateOpen" />
</template>

<style scoped>
.projects {
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: 32px 48px;
}

.projects__title {
  margin: 0;
  color: var(--color-text-primary);
  font-size: var(--font-size-h2);
  font-weight: var(--font-weight-semibold);
  line-height: 23px;
}

.projects__summary {
  margin: 20px 0 0;
  color: var(--color-text-muted);
  font-size: var(--font-size-body);
  line-height: 16px;
}

/* Сетка слева, подсказка и лента справа — макет 17:765. */
.projects__split {
  display: flex;
  align-items: flex-start;
  gap: 24px;
  margin-top: 20px;
}

/* Две колонки по 354 при ширине 736 — как в макете; на узком экране ряд
   разбирается сам. */
.projects__grid {
  display: grid;
  flex: 1;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 20px 21px;
  min-width: 0;
}

.projects__side {
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  gap: 16px;
  width: 344px;
}

/* Ниже 1100 колонка уезжает под сетку: рядом с двумя карточками проектов ей
   уже не хватает места, а на телефоне она и вовсе занимает экран целиком. */
@media (max-width: 1100px) {
  .projects__split {
    flex-direction: column;
  }

  .projects__side {
    width: 100%;
  }
}

.projects__skeleton {
  height: 148px;
  border-radius: var(--radius-xl);
  background: var(--color-bg-muted);
}

.projects__search {
  width: 220px;
}

</style>
